import React, { useEffect, useRef } from "react";
import { Box, Flex, Spinner, Text, useDisclosure, useToast } from "@chakra-ui/react";

import { useLang } from "../../../Hooks";
import { ReportHeader } from "../Shared/Components/ReportHeader";
import { ReportToolbar } from "../Shared/Components/ReportToolbar";
import { reportToolbarStyles } from "../Shared/Styles/reportToolbarStyles";
import CashFlowActions from "./Components/CashFlowActions";
import CashFlowFilters from "./Components/CashFlowFilters";
import CashFlowTable from "./Components/CashFlowTable";
import CustomRangeModal from "./Components/CustomRangeModal";
import useCashFlowData from "./hooks/useCashFlowData";

export default function CashFlowReport() {
  const toast = useToast();
  const { texts } = useLang();
  const customRange = useDisclosure();
  const data = useCashFlowData(texts, toast);
  const { fetchReport } = data;
  const initialFiltersRef = useRef(data.filters);

  useEffect(() => {
    void fetchReport(initialFiltersRef.current);
  }, [fetchReport]);

  return (
    <Box p={2} bg="var(--bg-primary)" minH="50vh" color="var(--text-primary)">
      <Flex {...reportToolbarStyles.headerRow}>
        <ReportHeader
          title={texts.reports?.cashFlow || "Cash Flow"}
          subtitle={texts.reports?.financialOverview || "Financial overview"}
        />
        <ReportToolbar>
        <CashFlowFilters
          filters={data.filters}
          dateRangeLabel={data.dateRangeLabel}
          onSelectRange={data.handleSelectRange}
          onOpenCustomRange={customRange.onOpen}
          onApplyFilters={data.handleApplyFilters}
          onCurrencyChange={data.handleCurrencyChange}
          texts={texts}
        />
        <CashFlowActions
          onExport={data.handleExport}
          isExporting={data.isExporting}
          texts={texts}
        />
        </ReportToolbar>
      </Flex>

      {data.isLoading ? (
        <Flex justify="center" align="center" h="300px">
          <Spinner size="lg" color="var(--accent-primary)" thickness="4px" />
          <Text ml={2} fontSize="sm">
            {texts.reports?.loadingReport || "Loading report..."}
          </Text>
        </Flex>
      ) : data.isReportEmpty ? (
        <Flex justify="center" align="center" h="300px" direction="column">
          <Text fontSize="md" color="var(--text-secondary)">
            {texts.reports?.noCashFlowData ||
              "No Cash Flow data found for the selected period and filters."}
          </Text>
        </Flex>
      ) : (
        <CashFlowTable data={data.transformedData} texts={texts} />
      )}

      <CustomRangeModal
        isOpen={customRange.isOpen}
        onClose={customRange.onClose}
        onApplyCustomRange={data.handleApplyCustomRange}
        initialFrom={data.filters.date_after}
        initialTo={data.filters.date_before}
        texts={texts}
      />
    </Box>
  );
}
